import { APICONFIG } from '@config/api.config';
import { azureSasAllowsBlobUpload, describeAzureSasPermissions, getAzureSasPermissions } from '@helper/azureSas';
import { uploadCompressFile } from '@helper/fileUpload';
import { APIService } from '@services/api/common-services';
import type { PresignedURLDetails } from '@services/hooks/enduser-details';
import { isProvisionalSiteVisitRecordingSiteId } from '@services/siteVisitRecording/siteVisitRecordingConstants';
import useSiteLeadStore from '@store/siteLeadStore';

export interface SiteBlobPresignedUrlRequest {
	siteId: number;
	filename: string;
}

export interface SiteVisitRecordingUploadPayload {
	clientSessionId: string;
	siteId: number | null;
	durationMs: number;
	audio: Blob;
	mimeType: string;
	membershipno?: string | null;
	bdeCode?: string | null;
	cmdiName?: string | null;
	sfid?: string | null;
}

export interface SiteVisitRecordingUploadResponse {
	pre_signed_url: string;
	normal_url?: string;
	filename: string;
}

function extensionFromMime(mime: string): string {
	if (mime.includes('mp4')) {
		return 'm4a';
	}
	if (mime.includes('webm')) {
		return 'webm';
	}
	return 'bin';
}

const VISIT_SFID_POLL_MS = 2000;
const VISIT_SFID_MAX_ATTEMPTS = 30;

function sleep(ms: number): Promise<void> {
	return new Promise((resolve) => {
		window.setTimeout(resolve, ms);
	});
}

function normalizeVisitSfid(sfid: string | null | undefined): string | null {
	const trimmed = sfid?.trim() ?? '';
	if (!trimmed || trimmed.toLowerCase() === 'unknown') {
		return null;
	}
	return trimmed;
}

async function fetchVisitSfid(siteVisitId: number): Promise<string | null> {
	const visit = await APIService.getData<{ sfid?: string | null }>(`${APICONFIG.SITE_VISIT}${siteVisitId}`);
	return normalizeVisitSfid(visit?.sfid);
}

/**
 * Recording filenames must include the visit SFID. Salesforce can assign it after the visit
 * is created, so poll the visit until it exists instead of uploading as "unknown".
 */
async function waitForVisitSfid(initialSfid: string | null | undefined): Promise<string> {
	const immediate = normalizeVisitSfid(initialSfid);
	if (immediate) {
		return immediate;
	}

	for (let attempt = 0; attempt < VISIT_SFID_MAX_ATTEMPTS; attempt += 1) {
		const store = useSiteLeadStore.getState();
		const storeSfid = normalizeVisitSfid(store.visitSfid);
		if (storeSfid) {
			return storeSfid;
		}

		const siteVisitId = store.siteVisitId;
		if (siteVisitId) {
			try {
				const visitSfid = await fetchVisitSfid(siteVisitId);
				if (visitSfid) {
					store.setVisitSfid(visitSfid);
					return visitSfid;
				}
			} catch {
				// Visit exists but SFID is not assigned yet.
			}
		}

		await sleep(VISIT_SFID_POLL_MS);
	}

	throw new Error('Visit recording was not saved because the visit SFID is not ready yet.');
}

export function siteVisitRecordingFilename(
	mimeType: string,
	siteId: number | null,
	membershipno: string | null,
	bdeCode: string | null,
	sfid: string
): string {
	const ext = extensionFromMime(mimeType);
	const siteIdStr = siteId ?? 'unknown';
	const membershipStr = membershipno ?? 'unknown';
	const bdeCodeStr = bdeCode ?? 'unknown';
	const sfidSafe = normalizeVisitSfid(sfid)?.replace(/[^a-zA-Z0-9]/g, '_');
	if (!sfidSafe) {
		throw new Error('Visit recording was not saved because the visit SFID is not ready yet.');
	}

	const datetime = Date.now();

	return `${siteIdStr}-${membershipStr}-${bdeCodeStr}-${sfidSafe}-${datetime}.${ext}`;
}

function normalizePresignedResponse(
	response: PresignedURLDetails | PresignedURLDetails[] | SiteBlobPresignedUrlRow | SiteBlobPresignedUrlRow[]
): PresignedURLDetails {
	const row = Array.isArray(response) ? response[0] : response;
	if (!row?.pre_signed_url) {
		throw new Error('Pre-signed URL missing from site-blob-presigned-url response');
	}
	return {
		pre_signed_url: row.pre_signed_url,
		normal_url: row.normal_url,
		filename: row.filename ?? '',
	};
}

/** Backend may return the same shape as common get-pre-signed-url or a single object. */
type SiteBlobPresignedUrlRow = PresignedURLDetails;

/** Same API site images use — returns SAS with write permission (`sp` includes `w`). */
export async function fetchCommonPresignedUrl(filename: string): Promise<PresignedURLDetails> {
	const rows = await APIService.postData<PresignedURLDetails[]>(APICONFIG.PRESIGNED_URL, [
		{ folder: 'sitelead', filename },
	]);
	const row = rows?.[0];
	if (!row?.pre_signed_url) {
		throw new Error('Pre-signed URL missing from get-pre-signed-url response');
	}
	return row;
}

export interface RenewSiteBlobPresignedUrlRequest {
	pre_signed_url: string;
}

export async function fetchRenewSiteBlobPresignedUrl(
	request: RenewSiteBlobPresignedUrlRequest
): Promise<PresignedURLDetails> {
	const formData = new FormData();
	formData.append('url', request.pre_signed_url);

	const response = await APIService.postFormData<
		PresignedURLDetails | PresignedURLDetails[] | SiteBlobPresignedUrlRow | SiteBlobPresignedUrlRow[]
	>(APICONFIG.RENEW_SITE_BLOB_PRESIGNED_URL, formData);

	return normalizePresignedResponse(response);
}

/**
 * Prefer site-blob-presigned-url (site-scoped path). If backend returns read-only SAS (`sp=r`),
 * fall back to common get-pre-signed-url (same as site image uploads).
 */
export async function fetchSiteBlobPresignedUrl(request: SiteBlobPresignedUrlRequest): Promise<PresignedURLDetails> {
	const formData = new FormData();
	formData.append('site_id', String(request.siteId));
	formData.append('filename', request.filename);

	const response = await APIService.postFormData<
		PresignedURLDetails | PresignedURLDetails[] | SiteBlobPresignedUrlRow | SiteBlobPresignedUrlRow[]
	>(APICONFIG.SITE_BLOB_PRESIGNED_URL, formData);

	const presigned = normalizePresignedResponse(response);

	// If the URL is an Azure SAS URL, check permissions.
	// GCP URLs don't use 'sp=' for permissions, so we bypass this check for GCP.
	if (presigned.pre_signed_url.includes('.blob.core.windows.net')) {
		const sp = getAzureSasPermissions(presigned.pre_signed_url);
		if (!azureSasAllowsBlobUpload(sp)) {
			// site-blob-presigned-url returned sp=r (read-only) — cannot PUT blocks; use image presigned API
			// eslint-disable-next-line no-console
			console.warn(
				`site-blob-presigned-url returned read-only SAS (sp=${describeAzureSasPermissions(sp)}). ` +
					'Falling back to get-pre-signed-url for upload. Backend should set sp to include write (w) on site-blob-presigned-url.'
			);
			return fetchCommonPresignedUrl(request.filename);
		}
	}

	return presigned;
}

/**
 * Same upload as site images (`uploadCompressFile` → `uploadFileToGcp`).
 */
export async function uploadSiteVisitRecordingToPresignedUrl(
	audio: Blob,
	presigned: PresignedURLDetails,
	mimeType: string
): Promise<SiteVisitRecordingUploadResponse> {
	const filename = presigned.filename || 'site-visit-recording.webm';
	const file = new File([audio], filename, { type: mimeType || audio.type || 'audio/webm' });

	await uploadCompressFile(file, presigned.pre_signed_url);

	return {
		pre_signed_url: presigned.pre_signed_url,
		normal_url: presigned.normal_url,
		filename,
	};
}

/** Presigned URL + block upload (used from site address Submit, mirroring image upload timing). */
export async function uploadSiteVisitRecording(
	payload: SiteVisitRecordingUploadPayload
): Promise<SiteVisitRecordingUploadResponse> {
	if (payload.siteId == null || isProvisionalSiteVisitRecordingSiteId(payload.siteId)) {
		throw new Error('site_id is required to upload site visit recording');
	}
	if (payload.audio.size <= 0) {
		throw new Error('Recording is empty; nothing to upload');
	}

	let membershipNo = payload.membershipno;

	if (!membershipNo && payload.siteId) {
		try {
			const visitData = await APIService.getData<{
				membership_no?: string;
				lead_membership_no?: string;
				account_id?: number;
			}>(`${APICONFIG.SITE_LEAD_OVERVIEW}${payload.siteId}/`);
			membershipNo = visitData?.membership_no || visitData?.lead_membership_no;

			if (!membershipNo && visitData?.account_id) {
				const endUserData = await APIService.getData<{ membershipno?: string }>(
					`${APICONFIG.ALL_ENDUSER_CONTRACTOR_DETAILS}/${visitData.account_id}`
				);
				membershipNo = endUserData?.membershipno;
			}
		} catch {
			// Membership is optional in the filename; visit SFID is not.
		}
	}

	const visitSfid = await waitForVisitSfid(payload.sfid || useSiteLeadStore.getState().visitSfid);

	const filename = siteVisitRecordingFilename(
		payload.mimeType,
		payload.siteId,
		membershipNo ?? null,
		payload.bdeCode ?? null,
		visitSfid
	);
	const presigned = await fetchSiteBlobPresignedUrl({ siteId: payload.siteId, filename });
	return uploadSiteVisitRecordingToPresignedUrl(payload.audio, presigned, payload.mimeType);
}
